import type { UserRef } from './api';
import { readZip } from './zip';

/**
 * Sites (2026-11-03): websites published in folders of their own at
 * `<sites address>/<folder>/`. The shapes the API sends, and what the pages work
 * out themselves — which files a site takes, the folders inside a site, and where
 * the files of a dropped folder or a .zip go.
 */

export const SITES_KEY = ['sites'] as const;

export interface SiteSummary {
  id: string;
  name: string;
  folder: string;
  description: string | null;
  /** `https://sites.viganium.net/<folder>/` — what the browser opens. */
  address: string;
  isLive: boolean;
  searchable: boolean;
  owner: UserRef;
  fileCount: number;
  bytes: number;
  /** It has its front page, `index.html` at the top: without it, its address answers 404. */
  hasIndex: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteFile {
  id: string;
  /** Where it sits in the site: `css/site.css`. */
  path: string;
  type: string;
  /** A page, a style sheet, a script or other text: the editor opens it. */
  text: boolean;
  size: number;
  /** New with every save; the editor sends it back, so as not to save over someone else's change. */
  version: string;
  updatedAt: string;
  uploadedBy: UserRef | null;
}

export interface SiteDetails extends SiteSummary {
  maxFiles: number;
  files: SiteFile[];
}

export interface SitesList {
  /** Where every site's address starts. */
  address: string;
  /** An administrator: every site, whoever looks after it. */
  managesAll: boolean;
  sites: SiteSummary[];
}

export type FolderCheck = { folder: string; problem: FolderProblem | 'TAKEN' | null; address: string };
export type FolderProblem = 'EMPTY' | 'TOO_SHORT' | 'TOO_LONG' | 'SHAPE' | 'RESERVED';

// ---------------------------------------------------------------------------
// What a site takes — the API's sites-rules.ts, which decides; this only spares an upload it would refuse
// ---------------------------------------------------------------------------

/** By extension: pages, styles, scripts, and what they load. */
export const SITE_EXTENSIONS: ReadonlySet<string> = new Set([
  '.html', '.htm', '.css', '.js', '.mjs', '.wasm',
  '.json', '.map', '.webmanifest', '.xml', '.txt', '.csv', '.vtt',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.svg', '.ico', '.bmp',
  '.woff', '.woff2', '.ttf', '.otf', '.eot',
  '.mp4', '.webm', '.ogv', '.mp3', '.ogg', '.wav', '.m4a',
  '.pdf',
]);

const MEDIA_EXTENSIONS: ReadonlySet<string> = new Set(['.mp4', '.webm', '.ogv', '.mp3', '.ogg', '.wav', '.m4a']);

/** What the editor writes: text a site is made of. */
export const TEXT_EXTENSIONS: ReadonlySet<string> = new Set([
  '.html', '.htm', '.css', '.js', '.mjs', '.json', '.map', '.webmanifest', '.xml', '.txt', '.csv', '.vtt', '.svg',
]);

/** The folder a computer leaves in a zip or a folder, never part of a site. */
const LEFT_BEHIND = /(^|\/)(__MACOSX|\.DS_Store|Thumbs\.db|desktop\.ini)(\/|$)/i;

export type SkipReason = 'HIDDEN' | 'TYPE' | 'NAME';

export function extensionOf(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot).toLowerCase();
}

export function isMedia(path: string): boolean {
  return MEDIA_EXTENSIONS.has(extensionOf(path));
}

/** Slashes between folders, nothing before, no `.` or empty folder. */
export function cleanPath(raw: string): string {
  return raw
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s !== '' && s !== '.')
    .join('/');
}

/** Why a site would not take this path, or null when it would — the API checks again. */
export function skipReason(path: string): SkipReason | null {
  const segments = path.split('/');
  if (LEFT_BEHIND.test(path) || segments.some((s) => s.startsWith('.'))) return 'HIDDEN';
  if (segments.some((s) => !s || s === '..' || /[\u0000-\u001f\u007f\\:*?"<>|#%]/.test(s) || s.trim() !== s)) return 'NAME';
  return SITE_EXTENSIONS.has(extensionOf(path)) ? null : 'TYPE';
}

/** Lower case letters, digits and hyphens: a name made into a folder, as the API suggests one. */
export function suggestFolder(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

/** A file's own address: the site's, then its path, each part escaped. */
export function fileAddress(site: { address: string }, path: string): string {
  return site.address + path.split('/').map(encodeURIComponent).join('/');
}

// ---------------------------------------------------------------------------
// Downloading: the whole site or a folder of it as a .zip, or one file
// ---------------------------------------------------------------------------

/** What to download: a folder of the site — '' is the whole site — or one file, by its path. */
export type DownloadTarget = { folder: string } | { path: string };

/** `POST /sites/:id/download`: a link that works for two minutes, and what it saves. */
export interface SiteDownload {
  url: string;
  /** What it is saved as: `spring-sale.zip`, `spring-sale-css.zip`, `logo.png`. */
  name: string;
  files: number;
  bytes: number;
}

/**
 * Has the browser save what a download link sends — straight into its
 * downloads, however large, as for any link it follows — without leaving the page.
 */
export function saveDownload(link: SiteDownload): void {
  const a = document.createElement('a');
  a.href = link.url;
  a.download = link.name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ---------------------------------------------------------------------------
// The folders inside a site
// ---------------------------------------------------------------------------

export interface SiteFolderRow {
  name: string;
  /** `css/vendor`. */
  path: string;
  files: number;
  bytes: number;
  updatedAt: string;
}

/** What is right inside a folder of the site ('' is its top): the folders in it, then the files. */
export function listing(files: SiteFile[], folder: string): { folders: SiteFolderRow[]; files: SiteFile[] } {
  const prefix = folder ? `${folder}/` : '';
  const folders = new Map<string, SiteFolderRow>();
  const here: SiteFile[] = [];
  for (const file of files) {
    if (!file.path.startsWith(prefix)) continue;
    const rest = file.path.slice(prefix.length);
    const slash = rest.indexOf('/');
    if (slash === -1) {
      here.push(file);
      continue;
    }
    const name = rest.slice(0, slash);
    const row = folders.get(name) ?? { name, path: prefix + name, files: 0, bytes: 0, updatedAt: file.updatedAt };
    row.files += 1;
    row.bytes += file.size;
    if (file.updatedAt > row.updatedAt) row.updatedAt = file.updatedAt;
    folders.set(name, row);
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    folders: [...folders.values()].sort(byName),
    files: here.sort((a, b) => byName({ name: a.path }, { name: b.path })),
  };
}

/** Whether a folder of the site exists — has a file in it, however deep. */
export function folderExists(files: SiteFile[], folder: string): boolean {
  return folder === '' || files.some((f) => f.path.startsWith(`${folder}/`));
}

// ---------------------------------------------------------------------------
// What goes up: picked files, a dropped folder, a .zip
// ---------------------------------------------------------------------------

/** One file to put in the site, at its path there. */
export interface Outgoing {
  path: string;
  size: number;
  blob: () => Promise<Blob>;
}

/**
 * What a computer leaves beside the files — a Mac's `__MACOSX/` and `.DS_Store`,
 * Windows' `Thumbs.db` — is dropped before anything else, unseen: in a zip made on
 * a Mac it would otherwise stand beside the site's own folder.
 */
export function withoutLeftovers<T extends { path: string }>(items: T[]): T[] {
  return items.filter((i) => !LEFT_BEHIND.test(i.path));
}

/**
 * A whole site handed over in a folder of its own — `my-site/index.html`,
 * `my-site/css/…` — goes in as the site, not as a folder inside it: the folder's
 * name is dropped when everything is in it and its `index.html` is right there.
 * Anything else keeps its folders.
 */
export function unwrapSite<T extends { path: string }>(items: T[]): T[] {
  const tops = new Set(items.map((i) => i.path.split('/')[0]));
  if (tops.size !== 1 || items.some((i) => !i.path.includes('/'))) return items;
  const [top] = tops;
  if (!items.some((i) => i.path === `${top}/index.html`)) return items;
  return items.map((i) => ({ ...i, path: i.path.slice(top.length + 1) }));
}

/** Files picked one by one: each by its own name. */
export function pickedFiles(files: File[]): Outgoing[] {
  return files.map((file) => ({ path: file.name, size: file.size, blob: async () => file }));
}

/** A folder picked (`webkitdirectory`): each file at its place in it. */
export function pickedFolder(files: File[]): Outgoing[] {
  return unwrapSite(
    withoutLeftovers(
      files.map((file) => ({
        path: cleanPath(file.webkitRelativePath || file.name),
        size: file.size,
        blob: async () => file,
      })),
    ),
  );
}

/** A .zip: each file in it at its place, a whole site unwrapped. */
export async function zipFiles(zip: Blob): Promise<Outgoing[]> {
  const entries = await readZip(zip);
  return unwrapSite(withoutLeftovers(entries.map((entry) => ({ path: cleanPath(entry.path), size: entry.size, blob: entry.read }))));
}

/**
 * What was dropped on the page: files, folders (walked through), and a .zip
 * (opened). Read from the drop's own entries while the drop event lasts — the
 * browser takes them away once it is over.
 */
export async function droppedFiles(transfer: DataTransfer): Promise<Outgoing[]> {
  const entries = Array.from(transfer.items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null);
  // A browser that cannot walk folders: the files themselves.
  if (entries.some((e) => e === null)) return expandZips(pickedFiles(Array.from(transfer.files)));

  const found: Outgoing[] = [];
  const walk = async (entry: FileSystemEntry, under: string): Promise<void> => {
    const path = under ? `${under}/${entry.name}` : entry.name;
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      found.push({ path, size: file.size, blob: async () => file });
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // A folder is read a batch at a time, until a batch comes back empty.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const child of batch) await walk(child, path);
    }
  };
  for (const entry of entries) await walk(entry!, '');
  const folders = entries.filter((e) => e!.isDirectory);
  const kept = withoutLeftovers(found);
  return expandZips(folders.length === 1 && entries.length === 1 ? unwrapSite(kept) : kept);
}

/** A .zip among them goes in as what is in it, where the .zip would have gone. */
async function expandZips(items: Outgoing[]): Promise<Outgoing[]> {
  const out: Outgoing[] = [];
  for (const item of items) {
    if (extensionOf(item.path) !== '.zip') {
      out.push(item);
      continue;
    }
    const at = item.path.includes('/') ? item.path.slice(0, item.path.lastIndexOf('/') + 1) : '';
    for (const inner of await zipFiles(await item.blob())) out.push({ ...inner, path: at + inner.path });
  }
  return out;
}

/** A new page's first lines, so a site can start from nothing but the editor. */
export function starterPage(title: string): string {
  const name = title.replace(/[<>&]/g, '');
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${name}</title>
</head>
<body>
  <h1>${name}</h1>
</body>
</html>
`;
}
